import {
  Component,
  input,
  output,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import {
  Municipio,
  MunicipioFiltro
} from 'src/app/domain/model/municipio/municipio';
import { MunicipioRepository } from 'src/app/domain/service/municipio/municipio-repository';
import {
  DEFAULT_ROWS_PER_PAGE
} from 'src/app/common/constants-ejb';
import { sanitizeText } from 'src/app/common/input-utils';
import { busquedaMunicipioPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BaseBusquedaPaginadaDialog } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

export interface MunicipioSeleccionado {
  cdmunicipio: string;
  cdsura?: string;
  dsmunicipio: string;
}

/**
 * Diálogo modal paginado para búsqueda de municipios.
 * Filtros: Código (DANE), Código Sura, Nombre.
 *
 * Patrón idéntico a BusquedaTareaDialogComponent.
 */
@Component({
  selector: 'app-busqueda-municipio-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ProgressSpinnerModule,
    DropdownModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [header]="titulo() || 'Búsqueda de Municipio'"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '58rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      <!-- Filtros -->
      <div
        class="border rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Código</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.codigo"
              class="w-full"
              maxlength="10"
              (change)="sanitizarCampo('codigo')"
              (keyup.enter)="buscar()"
            />
          </div>
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Código Sura</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.codigoSura"
              class="w-full"
              maxlength="10"
              (change)="sanitizarCampo('codigoSura')"
              (keyup.enter)="buscar()"
            />
          </div>
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Nombre</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.nombre"
              class="w-full"
              maxlength="100"
              (change)="sanitizarCampo('nombre')"
              (keyup.enter)="buscar()"
            />
          </div>
        </div>
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 mt-4 w-full"
        >
          <button
            pButton
            icon="pi pi-search"
            label="Buscar"
            class="p-button-sm min-h-[44px]"
            (click)="buscar()"
            [disabled]="loading()"
          ></button>
          <button
            pButton
            icon="pi pi-eraser"
            label="Limpiar"
            class="p-button-sm p-button-secondary p-button-outlined min-h-[44px]"
            (click)="limpiarFiltros()"
          ></button>
        </div>
      </div>

      <!-- Mensaje de estado -->
      @if (mensaje()) {
        <div
          class="p-3 mb-3 rounded"
          [ngClass]="{
            'bg-yellow-100 text-yellow-800': mensajeTipo() === 'warning',
            'bg-blue-100 text-blue-800': mensajeTipo() === 'info',
            'bg-red-100 text-red-800': mensajeTipo() === 'error'
          }"
        >
          <i
            class="pi mr-2"
            [ngClass]="{
              'pi-exclamation-triangle': mensajeTipo() === 'warning',
              'pi-info-circle': mensajeTipo() === 'info',
              'pi-times-circle': mensajeTipo() === 'error'
            }"
          ></i>
          {{ mensaje() }}
        </div>
      }

      <!-- Loading -->
      @if (loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      }

      <!-- Tabla de resultados -->
      @if (!loading() && items().length > 0) {
        <div
          class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
        >
          <span>Listado de Municipios</span>
          @if (totalRegistros() > 0) {
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
              {{ totalRegistros() }}
            </span>
          }
        </div>
        <div class="md:hidden space-y-3 p-3">
          @for (item of items(); track item.cdmunicipio) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Nombre
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(item.dsmunicipio)"
                >
                  {{ textoTabla(item.dsmunicipio) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Código:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.cdmunicipio)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Código Sura:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.cdsura)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Departamento:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.dsdepartamento)
                  }}</span>
                </div>
              </div>
              <p-button
                label="Seleccionar"
                icon="pi pi-check"
                size="small"
                styleClass="w-full min-h-[44px]"
                (onClick)="seleccionarItem(item)"
              />
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="items()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [scrollHeight]="tableConfig().responsive.scrollHeight || '300px'"
            [tableStyle]="{
              'min-width': tableConfig().responsive.minWidth || 'max-content'
            }"
            [styleClass]="tableStyleClass()"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Código">Código</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Código Sura"
                    >Código Sura</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Nombre">Nombre</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Departamento"
                    >Departamento</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Seleccionar"
                    >Seleccionar</span
                  >
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.cdmunicipio)"
                    >{{ textoTabla(item.cdmunicipio) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.cdsura)"
                    >{{ textoTabla(item.cdsura) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.dsmunicipio)"
                    >{{ textoTabla(item.dsmunicipio) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.dsdepartamento)"
                    >{{ textoTabla(item.dsdepartamento) }}</span
                  >
                </td>
                <td>
                  <button
                    pButton
                    label="Seleccionar"
                    class="p-button-sm p-button-text"
                    (click)="seleccionarItem(item)"
                  ></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <app-busqueda-paginador
          [currentPage]="paginaActual()"
          [totalPages]="totalPaginas()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptions"
          [selectedPageSize]="selectedPageSize"
          (pageSizeChange)="onSelectedPageSizeChange($event)"
          (first)="irPrimeraPagina()"
          (previous)="loadPreviousPage()"
          (next)="loadNextPage()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      }

      @if (!loading() && busquedaRealizada && items().length === 0) {
        <div class="text-center text-gray-500 p-4 border rounded">
          No hay datos
        </div>
      }

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="onCancelar()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaMunicipioDialogComponent extends BaseBusquedaPaginadaDialog<Municipio> {
  private readonly municipioRepository = inject(MunicipioRepository);

  titulo = input<string>('');

  onSelect = output<MunicipioSeleccionado>();
  onClose = output<void>();

  visible = true;
  filtro: MunicipioFiltro = {};

  readonly tableConfig = computed(() =>
    busquedaMunicipioPreset.createConfig({
      data: this.items(),
      style: {
        tableClassName: 'tabla-busqueda-municipio',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se encontraron municipios'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '300px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de municipios',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = this.buildTableStyleClass(this.tableConfig);

  sanitizarCampo(campo: keyof MunicipioFiltro): void {
    this.filtro[campo] = sanitizeText(this.filtro[campo]);
  }

  private sanitizarFiltros(): void {
    this.sanitizarCampo('codigo');
    this.sanitizarCampo('codigoSura');
    this.sanitizarCampo('nombre');
  }

  async buscar(): Promise<void> {
    this.sanitizarFiltros();
    this.mensaje.set('');
    this.busquedaRealizada = true;
    // Búsqueda nueva: siempre página 1
    await this.ejecutarBusqueda(0);
  }

  // Los botones Anterior/Siguiente dependen de la respuesta del servidor
  // (skill frontend-girop §17.7), por eso se sobreescriben los derivados de
  // la base (que los calcula desde la página actual).
  override hasPrevious = signal(false);
  override hasNext = signal(false);

  /** Cursor devuelto por el backend para pedir la página siguiente */
  private nextCursorActual = signal<string | null>(null);

  /** Cursor devuelto por el backend para pedir la página anterior */
  private previousCursorActual = signal<string | null>(null);

  private async buscarPagina(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina = 1
  ): Promise<void> {
    this.loading.set(true);
    this.mensaje.set('');

    try {
      const resp = await this.municipioRepository.search(
        this.filtro,
        cursor,
        this.pageSize(),
        direction
      );
      const content = resp.content ?? [];

      this.items.set(content);
      this.totalRegistros.set(resp.totalElements ?? 0);
      this.nextCursorActual.set(resp.nextCursor ?? null);
      this.previousCursorActual.set(resp.previousCursor ?? null);

      this.hasNext.set(resp.hasNext ?? false);
      this.hasPrevious.set(resp.hasPrevious ?? false);

      // paginaActual solo alimenta las etiquetas y cambia únicamente tras una
      // respuesta exitosa. Con LAST se calcula con el total del servidor.
      if (direction === 'LAST') {
        const size = this.pageSize();
        const total = resp.totalElements ?? 0;
        this.paginaActual.set(
          size > 0 && total > 0 ? Math.max(1, Math.ceil(total / size)) : 1
        );
      } else {
        this.paginaActual.set(pagina);
      }

      if (content.length === 0) {
        this.mensaje.set('No se encontraron municipios');
        this.mensajeTipo.set('warning');
      }
    } catch (error) {
      this.mensaje.set(
        this.resolverMensajeErrorBusqueda(error, 'Error al buscar municipios')
      );
      this.mensajeTipo.set('error');
      this.items.set([]);
      this.totalRegistros.set(0);
    } finally {
      this.loading.set(false);
    }
  }

  protected async ejecutarBusqueda(_start: number): Promise<void> {
    await this.buscarPagina(null, 'NEXT', 1);
  }

  override async irPrimeraPagina(): Promise<void> {
    if (this.loading()) {
      return;
    }
    await this.buscarPagina(null, 'NEXT', 1);
  }

  override async loadPreviousPage(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) {
      return;
    }
    await this.buscarPagina(
      this.previousCursorActual(),
      'PREVIOUS',
      Math.max(1, this.paginaActual() - 1)
    );
  }

  override async loadNextPage(): Promise<void> {
    if (this.loading() || !this.hasNext()) {
      return;
    }
    await this.buscarPagina(
      this.nextCursorActual(),
      'NEXT',
      this.paginaActual() + 1
    );
  }

  override async irUltimaPagina(): Promise<void> {
    if (this.loading() || this.totalRegistros() === 0) {
      return;
    }
    await this.buscarPagina(null, 'LAST');
  }

  limpiarFiltros(): void {
    this.filtro = {};
    this.items.set([]);
    this.clearMensajes();
    this.resetPaginacion();
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.busquedaRealizada = false;
  }

  seleccionarItem(item: Municipio): void {
    this.onSelect.emit({
      cdmunicipio: item.cdmunicipio,
      cdsura: item.cdsura,
      dsmunicipio: item.dsmunicipio
    });
    this.visible = false;
  }

  onCancelar(): void {
    this.visible = false;
    this.onClose.emit();
  }

}
